// Popover-based multi-select used in the locations FilterStrip.
// Replaces inline-chip Autocomplete with a clean button trigger:
//   [icon] Label                    ← idle
//   [icon] Label · 5  ↓             ← with selections
// Clicking opens a popover with:
//   - search input
//   - virtualised checkbox list (filtered by search)
//   - "Select all visible" / "Clear" actions
// Designed to read modern + consistent with the rest of the strip.

import { useMemo, useRef, useState, type MouseEvent } from 'react'
import {
  Box, Button, Checkbox, IconButton, InputAdornment, Popover, Stack,
  TextField, Typography, alpha,
} from '@mui/material'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { AnimatePresence, motion } from 'framer-motion'

export interface MultiFilterButtonProps {
  icon: React.ReactNode
  label: string
  options: string[]
  value: string[]
  onChange: (v: string[]) => void
  /** When this dimension drives the colour mode, accent the button. */
  accent?: boolean
  /** Optional swatch color per option — used as a small dot on each row. */
  swatchForValue?: (v: string) => string | undefined
}

export function MultiFilterButton({
  icon, label, options, value, onChange, accent = false, swatchForValue,
}: MultiFilterButtonProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const open = Boolean(anchor)
  const valueSet = useMemo(() => new Set(value), [value])
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.toLowerCase().includes(q))
  }, [options, query])

  function toggle(v: string) {
    if (valueSet.has(v)) onChange(value.filter((x) => x !== v))
    else onChange([...value, v])
  }
  function selectAllVisible() {
    const next = new Set(valueSet)
    for (const o of filtered) next.add(o)
    onChange(Array.from(next))
  }
  function clear() { onChange([]) }

  const selectedCount = value.length
  const hasSelections = selectedCount > 0

  return (
    <>
      <Button
        onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchor(e.currentTarget)}
        disableRipple
        sx={{
          height: 38,
          px: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: 13,
          fontWeight: 600,
          color: hasSelections || accent ? 'primary.main' : 'text.secondary',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: hasSelections
            ? (t) => alpha(t.palette.primary.main, 0.45)
            : accent
              ? 'primary.main'
              : 'divider',
          borderWidth: accent ? 1.5 : 1,
          minWidth: 120,
          gap: 0.75,
          transition: 'all 180ms ease',
          '&:hover': {
            bgcolor: 'background.paper',
            borderColor: (t) => alpha(t.palette.primary.main, 0.55),
            boxShadow: (t) => `0 0 0 3px ${alpha(t.palette.primary.main, 0.1)}`,
          },
        }}
        endIcon={
          <KeyboardArrowDownRoundedIcon
            sx={{
              fontSize: 18,
              transition: 'transform 200ms ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        }
      >
        <Box sx={{
          display: 'inline-flex', alignItems: 'center',
          color: hasSelections || accent ? 'primary.main' : 'text.disabled',
        }}>
          {icon}
        </Box>
        <Box component="span" sx={{ fontWeight: 700 }}>{label}</Box>
        <AnimatePresence>
          {hasSelections && (
            <Box
              component={motion.span}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 380, damping: 22 }}
              sx={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 20, height: 20, px: 0.6,
                fontSize: 11, fontWeight: 800,
                bgcolor: 'primary.main', color: 'primary.contrastText',
                borderRadius: '999px',
              }}
            >
              {selectedCount}
            </Box>
          )}
        </AnimatePresence>
      </Button>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => { setAnchor(null); setQuery('') }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              width: 300, maxWidth: '90vw',
              borderRadius: 2.5,
              border: '1px solid', borderColor: 'divider',
              boxShadow: (t) => t.palette.mode === 'light'
                ? `0 18px 44px -12px rgba(15, 23, 42, 0.3), 0 4px 10px -4px rgba(15, 23, 42, 0.12)`
                : `0 18px 44px -12px rgba(0, 0, 0, 0.65), 0 4px 10px -4px rgba(0, 0, 0, 0.5)`,
              overflow: 'hidden',
            },
          },
        }}
        TransitionProps={{ onEntered: () => searchRef.current?.focus() }}
      >
        {/* Header — title + close */}
        <Stack direction="row" alignItems="center" sx={{
          px: 1.5, py: 1,
          background: (t) => `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.12)} 0%, ${alpha(t.palette.primary.main, 0.04)} 100%)`,
          borderBottom: '1px solid', borderColor: 'divider',
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{
              fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6,
              textTransform: 'uppercase', color: 'text.disabled', lineHeight: 1.1,
            }}>
              Filter by
            </Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, lineHeight: 1.25 }}>
              {label}
              {hasSelections && (
                <Box component="span" sx={{
                  ml: 0.75, fontSize: 11.5, color: 'primary.main', fontWeight: 700,
                }}>
                  · {selectedCount} selected
                </Box>
              )}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => { setAnchor(null); setQuery('') }}
            sx={{ color: 'text.disabled' }}>
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        {/* Search */}
        <Box sx={{ px: 1.25, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            inputRef={searchRef}
            size="small"
            fullWidth
            placeholder={`Search ${label.toLowerCase()}…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: 13, borderRadius: 1.5,
                bgcolor: (t) => alpha(t.palette.text.primary, 0.03),
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'divider' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' },
              },
            }}
          />
        </Box>

        {/* Options list */}
        <Box sx={{
          maxHeight: 320, overflowY: 'auto',
          py: 0.5,
        }}>
          {filtered.length === 0 ? (
            <Typography sx={{
              fontSize: 12.5, color: 'text.disabled',
              px: 2, py: 3, textAlign: 'center', fontStyle: 'italic',
            }}>
              {query ? `No matches for “${query}”` : `No ${label.toLowerCase()} values yet`}
            </Typography>
          ) : (
            filtered.map((opt) => {
              const selected = valueSet.has(opt)
              const swatch = swatchForValue?.(opt)
              return (
                <Stack
                  key={opt}
                  direction="row" alignItems="center" spacing={1}
                  onClick={() => toggle(opt)}
                  sx={{
                    mx: 0.5, px: 1, py: 0.5, borderRadius: 1.5,
                    cursor: 'pointer',
                    bgcolor: selected
                      ? (t) => alpha(t.palette.primary.main, 0.08)
                      : 'transparent',
                    '&:hover': {
                      bgcolor: (t) => alpha(t.palette.primary.main, selected ? 0.12 : 0.06),
                    },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={selected}
                    disableRipple
                    icon={
                      <Box sx={{
                        width: 16, height: 16, borderRadius: 0.75,
                        border: '1.5px solid', borderColor: 'divider',
                      }} />
                    }
                    checkedIcon={
                      <Box sx={{
                        width: 16, height: 16, borderRadius: 0.75,
                        bgcolor: 'primary.main',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <CheckRoundedIcon sx={{ fontSize: 13, color: 'primary.contrastText' }} />
                      </Box>
                    }
                    sx={{ p: 0, mr: 0.5 }}
                  />
                  {swatch && (
                    <Box sx={{
                      width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                      bgcolor: swatch,
                      border: '1.5px solid',
                      borderColor: (t) => alpha(t.palette.background.paper, 0.9),
                      boxShadow: `0 1px 3px ${alpha(swatch, 0.5)}`,
                    }} />
                  )}
                  <Typography sx={{
                    flex: 1, fontSize: 12.5,
                    fontWeight: selected ? 700 : 500,
                    color: selected ? 'text.primary' : 'text.secondary',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {opt}
                  </Typography>
                </Stack>
              )
            })
          )}
        </Box>

        {/* Footer actions */}
        <Stack direction="row" alignItems="center" justifyContent="space-between"
          sx={{
            px: 1.5, py: 0.85,
            borderTop: '1px solid', borderColor: 'divider',
            bgcolor: (t) => alpha(t.palette.text.primary, 0.02),
          }}>
          <Stack direction="row" spacing={0.5}>
            <Button size="small" disableRipple onClick={clear}
              disabled={!hasSelections}
              sx={{
                fontSize: 11.5, fontWeight: 700, textTransform: 'none',
                color: hasSelections ? 'text.secondary' : 'text.disabled',
                px: 1, minWidth: 0,
                '&:hover': { color: 'error.main', bgcolor: 'transparent' },
              }}>
              Clear
            </Button>
            <Box sx={{ color: 'text.disabled', fontSize: 11 }}>·</Box>
            <Button size="small" disableRipple onClick={selectAllVisible}
              disabled={filtered.length === 0}
              sx={{
                fontSize: 11.5, fontWeight: 700, textTransform: 'none',
                color: 'text.secondary', px: 1, minWidth: 0,
                '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
              }}>
              {query ? `Select ${filtered.length}` : 'Select all'}
            </Button>
          </Stack>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 600 }}>
            {selectedCount}/{options.length}
          </Typography>
        </Stack>
      </Popover>
    </>
  )
}
